import {
  htmlHasInvalidVariablePlaceholders,
  htmlHasLoneVariableDelimiters,
  htmlMissingRequiredVariables,
  isHtmlEffectivelyEmpty,
  type EmailFieldErrors,
} from '@kula-ai/components';
import { INTERVIEW_ACTIVITY_COPY } from '../copy';
import type {
  InterviewCommunicationFormState,
  InterviewVariablesByCategory,
} from './types';
import { SUBJECT_MAX_LENGTH } from './types';

const REQUIRED_SELF_SCHEDULE_VARIABLE = 'interview.self_schedule_link';
/**
 * Availability email must carry the share-availability link or the candidate has
 * no way to open the page. BE enforces this on send (`AtsCandidateAvailability`
 * → `err_availability_link_required_in_email_body`) but NOT on the plan save, so
 * we guard it here to keep saved plan templates usable.
 */
const REQUIRED_AVAILABILITY_VARIABLE = 'interview.availability_link';

export type InterviewCommunicationValidationErrors = {
  candidateCalendar?: EmailFieldErrors;
  candidateEmail?: EmailFieldErrors;
  interviewerCalendar?: EmailFieldErrors;
  interviewerEmail?: EmailFieldErrors;
  selfSchedule?: EmailFieldErrors;
  availability?: EmailFieldErrors;
};

/**
 * Variables context for validating template merge tags in editor content.
 * Each key maps to the variables for a specific communication block.
 */
export type CommunicationValidationVariables = {
  candidateCalendar?: InterviewVariablesByCategory;
  interviewerCalendar?: InterviewVariablesByCategory;
  candidateEmail?: InterviewVariablesByCategory;
  interviewerEmail?: InterviewVariablesByCategory;
  selfSchedule?: InterviewVariablesByCategory;
  availability?: InterviewVariablesByCategory;
};

/** Optional flags — Schedule Interview manual/adhoc omits self-schedule UI but shares this validator. */
export type ValidateInterviewCommunicationOptions = {
  /**
   * When `false`, the self-schedule email block is not validated (Schedule Interview
   * manual/adhoc: that panel is not shown). Default `true` (Interview Activity wizard).
   */
  validateSelfSchedule?: boolean;
  /**
   * When `false`, the candidate-availability email block is not validated (Schedule
   * Interview: that panel is not shown — only the InterviewActivity plan editor renders
   * it). Default `true` (Interview Activity wizard).
   */
  validateAvailability?: boolean;
};

function subjectError(subject: string): string | undefined {
  const t = subject.trim();
  if (!t) {
    return 'Subject is required';
  }
  if (t.length > SUBJECT_MAX_LENGTH) {
    return `Subject must be at most ${SUBJECT_MAX_LENGTH} characters`;
  }
  return undefined;
}

/**
 * Build a `Set` of allowed variable keys from a flat-by-category list. While
 * merge-variable queries are still loading, buckets default to `{}` —
 * returning an empty set means callers skip allowlist checks (so an empty
 * variables map doesn't mark every `{{…}}` invalid and block the wizard).
 */
function allowedKeysFromVariables(
  variables: InterviewVariablesByCategory | undefined
): Set<string> {
  if (!variables) return new Set();
  return new Set(Object.values(variables).flat());
}

function hasInvalidVariables(
  content: string,
  variables: InterviewVariablesByCategory | undefined
): boolean {
  const allowed = allowedKeysFromVariables(variables);
  if (allowed.size === 0) return false;
  return htmlHasInvalidVariablePlaceholders(content, allowed);
}

function hasLoneDelimiters(content: string): boolean {
  return htmlHasLoneVariableDelimiters(content);
}

function hasRequiredVariable(content: string, variableName: string): boolean {
  return htmlMissingRequiredVariables(content, [variableName]).length === 0;
}

function validateEmailBlock(
  enabled: boolean,
  subject: string,
  content: string,
  from: string,
  variables?: InterviewVariablesByCategory
): EmailFieldErrors | undefined {
  if (!enabled) {
    return undefined;
  }
  const errors: EmailFieldErrors = {};
  const s = subjectError(subject);
  if (s) {
    errors.subject = s;
  } else if (
    hasLoneDelimiters(subject) ||
    hasInvalidVariables(subject, variables)
  ) {
    errors.subject = INTERVIEW_ACTIVITY_COPY.validation.subjectInvalidVariable;
  }
  if (!from?.trim()) {
    errors.from = 'From is required';
  }
  if (isHtmlEffectivelyEmpty(content)) {
    errors.content = 'Body is required';
  } else if (
    hasLoneDelimiters(content) ||
    hasInvalidVariables(content, variables)
  ) {
    errors.content = INTERVIEW_ACTIVITY_COPY.validation.bodyInvalidVariable;
  }
  return Object.keys(errors).length ? errors : undefined;
}

function validateCalendarBlock(
  content: string,
  variables?: InterviewVariablesByCategory
): EmailFieldErrors | undefined {
  if (isHtmlEffectivelyEmpty(content)) {
    return { content: 'Body is required' };
  }
  if (hasLoneDelimiters(content) || hasInvalidVariables(content, variables)) {
    return { content: INTERVIEW_ACTIVITY_COPY.validation.bodyInvalidVariable };
  }
  return undefined;
}

/**
 * Returns field errors for each block; all clear when the form is valid.
 */
export function validateInterviewCommunicationForm(
  state: InterviewCommunicationFormState,
  variables?: CommunicationValidationVariables,
  options?: ValidateInterviewCommunicationOptions
): InterviewCommunicationValidationErrors {
  const out: InterviewCommunicationValidationErrors = {};
  const shouldValidateSelfSchedule = options?.validateSelfSchedule !== false;
  const shouldValidateAvailability = options?.validateAvailability !== false;

  const candCal = validateCalendarBlock(
    state.candidateCalendar.content,
    variables?.candidateCalendar
  );
  if (candCal) {
    out.candidateCalendar = candCal;
  }

  const intCal = validateCalendarBlock(
    state.interviewerCalendar.content,
    variables?.interviewerCalendar
  );
  if (intCal) {
    out.interviewerCalendar = intCal;
  }

  const candEmail = validateEmailBlock(
    state.candidateEmailEnabled,
    state.candidateEmail.subject,
    state.candidateEmail.content,
    state.candidateEmail.from,
    variables?.candidateEmail
  );
  if (candEmail) {
    out.candidateEmail = candEmail;
  }

  const intEmail = validateEmailBlock(
    state.interviewerEmailEnabled,
    state.interviewerEmail.subject,
    state.interviewerEmail.content,
    state.interviewerEmail.from,
    variables?.interviewerEmail
  );
  if (intEmail) {
    out.interviewerEmail = intEmail;
  }

  let selfErrors =
    shouldValidateSelfSchedule && state.selfScheduleEnabled
      ? validateEmailBlock(
          true,
          state.selfSchedule.subject,
          state.selfSchedule.content,
          state.selfSchedule.from,
          variables?.selfSchedule
        )
      : undefined;
  if (
    shouldValidateSelfSchedule &&
    state.selfScheduleEnabled &&
    !isHtmlEffectivelyEmpty(state.selfSchedule.content) &&
    !hasRequiredVariable(
      state.selfSchedule.content,
      REQUIRED_SELF_SCHEDULE_VARIABLE
    )
  ) {
    const existing = selfErrors ?? {};
    // Self-schedule link is mandatory. Keep this as the primary message so the
    // panel can render a clear, actionable hint instead of a generic invalid-variable error.
    existing.content = `Body must include {{${REQUIRED_SELF_SCHEDULE_VARIABLE}}}`;
    selfErrors = existing;
  }
  if (selfErrors) {
    out.selfSchedule = selfErrors;
  }

  // Candidate-availability request email — standard email block PLUS the
  // mandatory `interview.availability_link` variable (the candidate's entry
  // point to the share-availability page), mirroring the self-schedule link.
  let availabilityErrors =
    shouldValidateAvailability && state.availabilityEnabled
      ? validateEmailBlock(
          true,
          state.availability.subject,
          state.availability.content,
          state.availability.from,
          variables?.availability
        )
      : undefined;
  if (
    shouldValidateAvailability &&
    state.availabilityEnabled &&
    !isHtmlEffectivelyEmpty(state.availability.content) &&
    !hasRequiredVariable(
      state.availability.content,
      REQUIRED_AVAILABILITY_VARIABLE
    )
  ) {
    const existing = availabilityErrors ?? {};
    // Keep this as the primary body message so the panel renders a clear,
    // actionable hint instead of a generic invalid-variable error.
    existing.content = `Body must include {{${REQUIRED_AVAILABILITY_VARIABLE}}}`;
    availabilityErrors = existing;
  }
  if (availabilityErrors) {
    out.availability = availabilityErrors;
  }

  return out;
}

export function hasValidationErrors(
  errors: InterviewCommunicationValidationErrors
): boolean {
  return Object.keys(errors).length > 0;
}
